// SPDX-License-Identifier: AGPL-3.0-or-later

import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {
	boosterBadgeMonths,
	boosterBadgeTier,
	boosterBadgeUrl,
	boosterNextTierAt,
	nextMonthStepAt,
	STEP_MONTHS,
} from '@app/features/boost/utils/BoosterBadge';
import {mediaUrl} from '@app/features/messaging/utils/MessagingUrlUtils';
import {
	type PremiumPackageMonths,
	premiumBadgeUrl,
	premiumPackageBadgeUrl,
	premiumPackageMonths,
} from '@app/features/premium/utils/PremiumBadgeAsset';
import type {User} from '@app/features/user/models/User';
import botDeveloperBadgeFallbackUrl from '@app/media/images/badges/bot-developer.svg';
import {UserPremiumTypes} from '@fluxer/constants/src/UserConstants';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import type {InventoryBadge} from './BadgeInventoryState';

export type BadgeTierState = 'earned' | 'current' | 'locked';
export type BadgeRarity = 'common' | 'rare' | 'epic' | 'legendary';
export type TieredBadgeKey = 'booster' | 'premium' | 'premium_package' | 'premium_sequence' | 'staff' | 'bot_developer';

export interface BadgeTier {
	key: string;
	// Visionary ranges have no art of their own; they show the label.
	iconUrl?: string;
	label: string;
	state: BadgeTierState;
	rarity: BadgeRarity;
	requirement?: string;
}

// The default rarity of each tier, the lowest tier first. The one place to change it.
export const BADGE_TIER_RARITY: Record<TieredBadgeKey, ReadonlyArray<BadgeRarity>> = {
	booster: ['common', 'common', 'common', 'rare', 'rare', 'epic', 'epic', 'legendary', 'legendary'],
	premium: ['common', 'common', 'common', 'rare', 'rare', 'epic', 'epic', 'legendary', 'legendary'],
	premium_package: ['rare', 'epic', 'legendary'],
	premium_sequence: ['rare', 'rare', 'epic', 'epic', 'legendary'],
	staff: [
		'common',
		'common',
		'common',
		'rare',
		'rare',
		'rare',
		'epic',
		'epic',
		'epic',
		'legendary',
		'legendary',
		'legendary',
	],
	bot_developer: ['common', 'common', 'rare', 'rare', 'epic', 'epic', 'legendary'],
};

export const PLUS_STEP_MONTHS = [1, 2, 3, 6, 9, 12, 18, 24, 36];
// Visionary numbers never change and a smaller one is rarer: the ranges, the most common first.
export const VISIONARY_RANGES: ReadonlyArray<readonly [number, number | null]> = [
	[1001, null],
	[501, 1000],
	[101, 500],
	[11, 100],
	[1, 10],
];

// Any tiered badge is a list of steps with the one the user holds; a new family (Plus, Visionary, partner) only needs
// its own function building this.
export interface BadgeTierLadder {
	tiers: ReadonlyArray<BadgeTier>;
	nextInDays: number | null;
}

const FIRST_DAY = msg({
	message: 'First day',
	comment: 'Label of the first server booster badge tier, shown from the first day of boosting.',
});
const MONTHS = msg({
	message: '{months, plural, one {# month} other {# months}}',
	comment: 'Label of a badge tier reached after this many months (booster) or bought as this many months of Plus.',
});
const BOOSTER_REQUIREMENT = msg({
	message: 'After {months, plural, one {# month} other {# months}} of boosting',
	comment: 'What a locked server booster badge tier needs: boosting a community without a break for this long.',
});
const PLUS_REQUIREMENT = msg({
	message: 'After {months, plural, one {# month} other {# months}} of unbroken Plus',
	comment: 'What a locked Plus badge tier needs: a Plus subscription running without a break for this long.',
});
const VISIONARY_REQUIREMENT = msg({
	message: 'Visionary numbers {range}',
	comment:
		'Which Visionary numbers hold this badge tier. Preserve {range}; a number range like #1–10 inserted by code.',
});
const BOT_DEVELOPER_REQUIREMENT = msg({
	message: 'From {count, plural, one {# server} other {# servers}}',
	comment:
		'What a locked bot developer badge tier needs: verified bots in at least this many distinct servers. Preserve {count}.',
});
const PACKAGE_REQUIREMENT = msg({
	message: 'Unlocks with the {months}-month package',
	comment: 'What a locked early supporter badge tier needs: buying the Plus package of this many months.',
});

const PACKAGE_LIMITED = msg({
	message: 'Limited edition · no longer available',
	comment: 'A locked early supporter badge tier after the tiers closed: nobody can buy it any more.',
});

const DAY_MS = 86_400_000;
const PACKAGE_MONTHS: ReadonlyArray<PremiumPackageMonths> = [1, 3, 12];

function ladder(
	family: TieredBadgeKey,
	steps: ReadonlyArray<Omit<BadgeTier, 'state' | 'rarity'>>,
	current: number,
): ReadonlyArray<BadgeTier> {
	return steps.map((step, index) => {
		const state: BadgeTierState = index < current ? 'earned' : index === current ? 'current' : 'locked';
		return {
			...step,
			state,
			rarity: BADGE_TIER_RARITY[family][index],
			requirement: state === 'locked' ? step.requirement : undefined,
		};
	});
}

const daysUntil = (next: Date | null, now: Date) =>
	next ? Math.ceil((next.getTime() - now.getTime()) / DAY_MS) : null;

export function boosterTiers(since: Date, now: Date, i18n: I18n): BadgeTierLadder {
	const next = boosterNextTierAt(since, now);
	return {
		tiers: ladder(
			'booster',
			STEP_MONTHS.map((months, index) => ({
				key: String(months),
				iconUrl: boosterBadgeUrl(index + 1),
				label: months === 0 ? i18n._(FIRST_DAY) : i18n._(MONTHS, {months}),
				requirement: i18n._(BOOSTER_REQUIREMENT, {months}),
			})),
			boosterBadgeTier(boosterBadgeMonths(since, now)) - 1,
		),
		nextInDays: daysUntil(next, now),
	};
}

// Unbroken Plus: premium_since on the public profile starts the run and is cleared when Plus lapses.
export function plusTiers(since: Date, now: Date, i18n: I18n): BadgeTierLadder {
	const months = boosterBadgeMonths(since, now);
	return {
		tiers: ladder(
			'premium',
			PLUS_STEP_MONTHS.map((step) => ({
				key: String(step),
				iconUrl: premiumBadgeUrl(),
				label: i18n._(MONTHS, {months: step}),
				requirement: i18n._(PLUS_REQUIREMENT, {months: step}),
			})),
			PLUS_STEP_MONTHS.filter((step) => months >= step).length - 1,
		),
		nextInDays: daysUntil(nextMonthStepAt(since, now, PLUS_STEP_MONTHS), now),
	};
}

export function visionaryTiers(sequence: number, i18n: I18n): BadgeTierLadder {
	return {
		tiers: ladder(
			'premium_sequence',
			VISIONARY_RANGES.map(([from, to]) => {
				const label = to === null ? `#${from}+` : `#${from}–${to}`;
				return {key: label, label, requirement: i18n._(VISIONARY_REQUIREMENT, {range: label})};
			}),
			VISIONARY_RANGES.findIndex(([from, to]) => sequence >= from && (to === null || sequence <= to)),
		),
		nextInDays: null,
	};
}

// closed: the tiers are no longer sold, so a locked one says so instead of naming its package.
export function packageTiers(months: PremiumPackageMonths, i18n: I18n, closed = false): BadgeTierLadder {
	return {
		tiers: ladder(
			'premium_package',
			PACKAGE_MONTHS.map((step) => ({
				key: String(step),
				iconUrl: premiumPackageBadgeUrl(step),
				label: i18n._(MONTHS, {months: step}),
				requirement: closed ? i18n._(PACKAGE_LIMITED) : i18n._(PACKAGE_REQUIREMENT, {months: step}),
			})),
			PACKAGE_MONTHS.indexOf(months),
		),
		nextInDays: null,
	};
}

// Admins name the staff and bot developer tiers in Turkish and English; every other language reads the English name.
const tierName = (tier: {tr: string; en: string}, i18n: I18n) => (i18n.locale.startsWith('tr') ? tier.tr : tier.en);

const STAFF_BADGE_URL = () => `${RuntimeConfig.staticCdnEndpoint}/badges/micup-staff-v2.webp?v=3`;

// An image uploaded in the admin panel wins; a tier without one shows the plain staff badge.
export function staffBadgeUrl(tier: number | null): string {
	const icon = tier ? RuntimeConfig.staffBadgeTiers[tier - 1]?.icon : null;
	return icon ? mediaUrl(`branding/staff-badge-${tier}/${icon}.webp?size=128`) : STAFF_BADGE_URL();
}

export function staffTierName(tier: number, i18n: I18n): string | null {
	const entry = RuntimeConfig.staffBadgeTiers[tier - 1];
	return entry ? tierName(entry, i18n) : null;
}

export function staffTiers(tier: number, i18n: I18n): BadgeTierLadder {
	return {
		tiers: ladder(
			'staff',
			RuntimeConfig.staffBadgeTiers.map((entry, index) => ({
				key: String(index + 1),
				iconUrl: staffBadgeUrl(index + 1),
				label: tierName(entry, i18n),
			})),
			tier - 1,
		),
		nextInDays: null,
	};
}

export function botDeveloperTier(guilds: number): number {
	return Math.max(1, RuntimeConfig.botDeveloperBadgeTiers.filter((tier) => guilds >= tier.threshold).length);
}

export function botDeveloperBadgeUrl(tier: number): string {
	const icon = RuntimeConfig.botDeveloperBadgeTiers[tier - 1]?.icon;
	return icon ? mediaUrl(`branding/bot-developer-badge-${tier}/${icon}.webp?size=128`) : botDeveloperBadgeFallbackUrl;
}

export function botDeveloperTierName(guilds: number, i18n: I18n): string {
	return tierName(RuntimeConfig.botDeveloperBadgeTiers[botDeveloperTier(guilds) - 1], i18n);
}

export function botDeveloperServersToNext(guilds: number): number | null {
	const next = RuntimeConfig.botDeveloperBadgeTiers.find((tier) => tier.threshold > guilds);
	return next ? next.threshold - guilds : null;
}

export function botDeveloperTiers(guilds: number, i18n: I18n): BadgeTierLadder {
	return {
		tiers: ladder(
			'bot_developer',
			RuntimeConfig.botDeveloperBadgeTiers.map((entry, index) => ({
				key: String(index + 1),
				iconUrl: botDeveloperBadgeUrl(index + 1),
				label: tierName(entry, i18n),
				requirement: i18n._(BOT_DEVELOPER_REQUIREMENT, {count: entry.threshold}),
			})),
			botDeveloperTier(guilds) - 1,
		),
		nextInDays: null,
	};
}

export function badgeTierLadder(badge: InventoryBadge, user: User, now: Date, i18n: I18n): BadgeTierLadder | null {
	if (badge.source === 'boost') return boosterTiers(new Date(badge.since), now, i18n);
	if (badge.source === 'bot_developer') return botDeveloperTiers(badge.guilds, i18n);
	if (badge.source === 'flag' && badge.key === 'staff') return badge.tier ? staffTiers(badge.tier, i18n) : null;
	if (badge.source !== 'premium' || badge.key !== 'premium_package') return null;
	// Hidden package flags do not name a tier; without the inventory's months there is nothing to show. The single
	// badge (0) has no tiers.
	const months = badge.months ?? premiumPackageMonths(user.flags);
	return months ? packageTiers(months, i18n) : null;
}

export type EarnableBadgeKey = 'premium' | 'premium_package' | 'premium_sequence' | 'booster';

export interface EarnableBadge {
	key: EarnableBadgeKey;
	iconUrl?: string;
	// The largest package on sale, for the package badge; 0 once every package gives the single badge.
	months?: PremiumPackageMonths;
	// 'premium' opens the Plus page; the booster badge is bought only inside a community, so it has no button.
	action: 'premium' | null;
}

export interface EarnableShop {
	settings: {premium_sales_open: boolean; premium_package_unified_since?: string | null} | null;
	packageEntries: ReadonlyArray<{months: number}>;
	visionary: {visible: boolean; remaining: number} | null;
	boost: unknown;
}

// Built-in badges the user lacks and can still get today; catalog badges have no way to earn them, so never.
export function earnableBadges({
	ownedKeys,
	user,
	shop,
	premiumVisible,
}: {
	ownedKeys: ReadonlySet<string>;
	user: User;
	shop: EarnableShop;
	premiumVisible: boolean;
}): Array<EarnableBadge> {
	const offers: Array<EarnableBadge> = [];
	if (premiumVisible) offers.push({key: 'premium', iconUrl: premiumBadgeUrl(), action: 'premium'});
	const largestPackage = PACKAGE_MONTHS.filter((months) =>
		shop.packageEntries.some((entry) => entry.months === months),
	).at(-1);
	// A lifetime member never gets the package badge (UserMappers drops it) and already holds Visionary.
	const lifetime = user.premiumType === UserPremiumTypes.LIFETIME;
	if (largestPackage && shop.settings?.premium_sales_open && !lifetime) {
		const months = shop.settings.premium_package_unified_since ? 0 : largestPackage;
		offers.push({key: 'premium_package', iconUrl: premiumPackageBadgeUrl(months), months, action: 'premium'});
	}
	if (!lifetime && shop.visionary?.visible && shop.visionary.remaining > 0)
		offers.push({key: 'premium_sequence', action: 'premium'});
	if (shop.boost != null) offers.push({key: 'booster', iconUrl: boosterBadgeUrl(1), action: null});
	return offers.filter((offer) => !ownedKeys.has(offer.key));
}
